import { motion } from "framer-motion";
import { QrCode, Wallet, Building2, Calculator, Users, Bell, Smartphone, Shield } from "lucide-react";

const FeaturesSection = () => {
  const features = [
    {
      icon: QrCode,
      title: "QR Code Earning",
      description: "Unique QR codes for each business. Customers scan, earn points instantly. No app download required.",
      highlight: true,
    },
    {
      icon: Wallet,
      title: "Digital Point Wallet",
      description: "Customers track all their points in one place. See balance, history, and available rewards anytime.",
    },
    {
      icon: Building2,
      title: "Multi-Business Membership",
      description: "Customers can join loyalty programs from multiple businesses. More reasons to engage, more data for you.",
    },
    {
      icon: Calculator,
      title: "Auto Reward Calculation",
      description: "Set your rules once. Points and rewards calculated automatically based on spending amount.",
    },
    {
      icon: Users,
      title: "Customer Analytics",
      description: "See who's visiting, how often, and what they're spending. Make data-driven decisions.",
    },
    {
      icon: Bell,
      title: "Smart Notifications",
      description: "Automated reminders when customers are close to rewards or haven't visited in a while.",
    },
    {
      icon: Smartphone,
      title: "Mobile-First Design",
      description: "Beautiful experience on any device. Your customers engage wherever they are.",
    },
    {
      icon: Shield,
      title: "Secure & Reliable",
      description: "Bank-level security for all transactions. Your data and your customers' data is safe.",
    },
  ];

  return (
    <section 
      id="features"
      className="section-padding bg-white"
      data-testid="features-section"
    >
      <div className="container-main">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-sm font-medium mb-4">
            Powerful Features
          </span>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="features-headline"
          >
            Everything You Need to Build Loyalty
          </h2>
          <p className="text-lg text-slate-600">
            Simple tools that work together to create lasting customer relationships.
          </p>
        </motion.div>

        {/* Features grid - Bento style */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className={`card-feature group ${
                feature.highlight 
                  ? 'md:col-span-2 lg:col-span-1 bg-gradient-to-br from-indigo-50 to-violet-50 border-indigo-100' 
                  : ''
              }`}
              data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className={`${feature.highlight ? 'icon-container' : 'icon-container'} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className={`w-6 h-6 ${feature.highlight ? 'text-indigo-600' : 'text-indigo-600'}`} />
              </div>
              <h3 
                className="text-lg font-bold text-slate-900 mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {feature.title}
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Feature highlight */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-16"
        >
          <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-3xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <h3 
                  className="text-2xl lg:text-3xl font-bold mb-4"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Ready in Minutes, Not Weeks
                </h3>
                <p className="text-indigo-100 text-lg mb-6">
                  No expensive hardware. No complicated setup. Just sign up, set your rewards, print your QR code, and you're ready to build loyalty.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
                    <svg className="w-4 h-4 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">5-minute setup</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
                    <svg className="w-4 h-4 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">No app needed</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
                    <svg className="w-4 h-4 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">Works on any device</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1595079836278-25b7ad6d5ddb?w=600&h=400&fit=crop" 
                  alt="Smartphone scanning QR code"
                  className="rounded-2xl shadow-2xl shadow-indigo-900/30 w-full object-cover"
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
